import { useEffect, useState } from 'preact/hooks';
import { Link } from 'wouter-preact';
import { ChevronRight, Menu as MenuIcon, Search } from 'lucide-preact';
import { Icon } from '../primitives/Icon';
import { IconButton } from '../primitives/Button';
import { Kbd } from '../primitives/Kbd';
import { NotificationCenter } from './NotificationCenter';
import { AssistantLauncher } from '@/components/assistant/AssistantLauncher';
import { ReviewModeToggle } from './ReviewMode';
import { SyncIndicator } from './SyncIndicator';
import { breadcrumb, pageTitle } from '@/stores/navigation';
import { openPalette } from '@/stores/commandPalette';
import { cn } from '@/lib/cn';

export interface TopBarProps {
  onOpenSidebar?: () => void;
  className?: string;
}

/**
 * TopBar — 56px sticky con breadcrumb + acciones globales.
 *
 * Refresh 2026-05-20 (shell rework):
 *   - WorkspaceSwitcher y UserMenu se movieron al sidebar (header y
 *     footer respectivamente).  Convención SaaS moderno (Linear /
 *     Notion / Stripe).
 *   - LiveIndicator (dot verde "sistema en línea") eliminado — era
 *     cosmético sin telemetría real detrás.
 *   - Acciones restantes: Search (⌘K) + Notifications.
 *   - Frosted glass cuando el contenido principal está scrolleado bajo
 *     el header (backdrop-blur + bg-canvas/85).
 */
export function TopBar({ onOpenSidebar, className }: TopBarProps) {
  const items = breadcrumb.value;
  const scrolled = useScrolled();

  return (
    <header
      className={cn(
        'sticky top-0 z-20 flex h-[var(--header-h)] shrink-0 items-center gap-3 px-4',
        'border-border-subtle duration-med ease-smooth border-b transition-[background-color,backdrop-filter]',
        scrolled ? 'bg-canvas/85 backdrop-blur-md backdrop-saturate-150' : 'bg-canvas',
        className,
      )}
    >
      {onOpenSidebar ? (
        <IconButton
          icon={MenuIcon}
          label="Abrir menú"
          size="sm"
          onClick={onOpenSidebar}
          variant="subtle"
        />
      ) : null}

      <Breadcrumb items={items} fallbackTitle={pageTitle.value} />

      <div className="flex shrink-0 items-center gap-2">
        <SearchTrigger />
        <SyncIndicator />
        <ReviewModeToggle />
        <AssistantLauncher />
        <NotificationCenter />
      </div>
    </header>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────

interface BreadcrumbProps {
  items: { label: string; href?: string }[];
  fallbackTitle: string;
}

function Breadcrumb({ items, fallbackTitle }: BreadcrumbProps) {
  if (items.length === 0) {
    return (
      <div className="flex min-w-0 flex-1 items-center">
        <p className="text-13 text-fg-strong truncate font-semibold tracking-tight">
          {fallbackTitle}
        </p>
      </div>
    );
  }
  if (items.length === 1) {
    return (
      <div className="flex min-w-0 flex-1 items-center">
        <p className="text-13 text-fg-strong truncate font-semibold tracking-tight">
          {items[0]!.label}
        </p>
      </div>
    );
  }
  return (
    <div className="flex min-w-0 flex-1 items-center">
      <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1 text-xs">
        {items.map((it, i) => {
          const isLast = i === items.length - 1;
          const hideOnMobile = !isLast;
          return (
            <div
              key={i}
              className={cn('flex shrink-0 items-center gap-1', hideOnMobile && 'hidden sm:flex')}
            >
              {it.href && !isLast ? (
                <Link
                  href={it.href}
                  className={cn(
                    'text-fg-muted rounded px-0.5 transition-colors outline-none',
                    'hover:text-fg-strong focus-visible:ring-brand/50 focus-visible:ring-2',
                  )}
                >
                  {it.label}
                </Link>
              ) : (
                <span
                  className={cn(
                    'truncate px-0.5',
                    isLast ? 'text-fg-strong font-semibold' : 'text-fg-muted',
                  )}
                >
                  {it.label}
                </span>
              )}
              {!isLast ? (
                <Icon
                  as={ChevronRight}
                  size={12}
                  className="text-fg-faint/70 shrink-0"
                  aria-hidden="true"
                />
              ) : null}
            </div>
          );
        })}
      </nav>
    </div>
  );
}

function SearchTrigger() {
  return (
    <>
      {/* Desktop: input-shaped pill. Más ancho para sentirse como un campo
          real de búsqueda, no un botón. Activación con click o ⌘K global. */}
      <button
        type="button"
        onClick={() => openPalette()}
        aria-label="Buscar (⌘K)"
        className={cn(
          'group hidden h-8 items-center gap-2 rounded-md md:inline-flex',
          'w-56 px-2.5 lg:w-72',
          'border-border-subtle bg-surface text-fg-faint border text-xs',
          'duration-med ease-smooth transition-[background-color,border-color,color,box-shadow]',
          'hover:border-border hover:bg-surface-hover hover:text-fg-muted',
          'focus-visible:border-brand/40 focus-visible:bg-surface focus-visible:shadow-focus focus-visible:text-fg-muted focus-visible:outline-none',
        )}
      >
        <Icon
          as={Search}
          size={13}
          className="text-fg-faint group-hover:text-fg-muted shrink-0 transition-colors"
        />
        <span className="flex-1 truncate text-left">Buscar productos, leads, pedidos…</span>
        <Kbd className="text-3xs shrink-0">⌘K</Kbd>
      </button>
      {/* Mobile: just an icon button. */}
      <IconButton
        icon={Search}
        label="Buscar"
        size="sm"
        variant="subtle"
        onClick={() => openPalette()}
        className="md:hidden"
      />
    </>
  );
}

// ─── Hooks ────────────────────────────────────────────────────────────────

/**
 * Detecta si el `<main>` que sigue al TopBar tiene scrollTop > 0. Lo usamos
 * para aplicar el frosted glass + border solo cuando hay contenido bajo el
 * header. Sin scroll, el TopBar se funde con el canvas.
 */
function useScrolled(threshold = 4) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    // El AppShell pone el scroll en <main id="main-content">. Si no existe
    // (tests, login layout, etc.), nos suscribimos a window como fallback.
    const mainEl = document.getElementById('main-content');
    const target: HTMLElement | Window = mainEl ?? window;
    const getScrollTop = () => (mainEl ? mainEl.scrollTop : window.scrollY);
    const onScroll = () => setScrolled(getScrollTop() > threshold);
    onScroll();
    target.addEventListener('scroll', onScroll, { passive: true });
    return () => target.removeEventListener('scroll', onScroll);
  }, [threshold]);
  return scrolled;
}
